import {
  EFormBlockBackground,
  EFormType,
  EFormBlockMediaLayout,
  getAnswerGrades,
  getAnswerOptedOut,
  getGradingOptOut,
  getGradingReflection,
  getGradingRows,
  getFormBlockTint,
  getGradingScale,
  getTextBlocks,
  isAnswerableQuestionType,
  isTeacherReviewedFormType,
  type TFormQuestionGroup,
} from '@onesource/schemas';
import {
  fetchSubmissionFeedback,
  getMediaUrl,
  type TFormQuestion,
  type TReviewAnswer,
  type TSubmissionFeedback,
  type TSubmissionResult,
} from '@onesource/services';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import React from 'react';

import { getFeedbackItems, wasFeedbackUpdated } from '../form-review/feedback-answer-text';
import { FEEDBACK_TINT } from '../form-review/feedback-palette';

// react-pdf hyphenates by default, which turns a narrow measure into "Struc-
// ture" in the middle of a workbook title. Words break to the next line instead.
Font.registerHyphenationCallback(word => [word]);

const BRAND = '#ff4d6d';
const BRAND_DEEP = '#c83a55';
const BRAND_TINT = '#fff0f3';
const INK = '#1a1a1a';
const BODY = '#333333';
const MUTED = '#6b6b6b';
// Small labels: #707070 keeps 4.5:1 on white and on CARD (#9b9b9b was ~2.7:1).
const SUBTLE = '#707070';
const HAIRLINE = '#ececec';
const CARD = '#fafafa';
const PILL_OFF_BG = '#ffffff';
const PILL_OFF_BORDER = '#dcdcdc';
const PILL_OFF_INK = '#777777';
/** Points. Keeps a portrait image from being squeezed into the page's remainder. */
const PDF_IMAGE_MAX_HEIGHT = 420;
const COVER_BADGE_BG = '#ffe1e8';

/** Used when a form turns the cover on without supplying its own wording. */
const DEFAULT_COVER_KICKER = 'Restore Your Core® · Professional Training';
const DEFAULT_COVER_BADGE = 'Interactive Workbook';

const styles = StyleSheet.create({
  page: {
    // The running header is absolutely positioned (like the footer), so the page
    // has to reserve its band — otherwise content that wraps onto a later page
    // starts at y=0 and prints underneath it.
    paddingTop: 56,
    paddingBottom: 56,
    paddingHorizontal: 0,
    fontSize: 11,
    color: BODY,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 18,
    borderBottom: `2pt solid ${BRAND}`,
  },
  logo: {
    width: 110,
    height: 48,
    objectFit: 'contain',
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  headerEyebrow: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 1.2,
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  headerEyebrowSpace: { color: BRAND, fontWeight: 'bold' },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: INK,
    maxWidth: 360,
    textAlign: 'right',
  },
  accentStrip: { height: 4, backgroundColor: BRAND_TINT },
  // Non-workbook forms print the branded logo header in flow (`fixed` repeats it
  // on every page), so the page needs no reserved band above it.
  brandedPage: { paddingTop: 0 },
  metaWrap: { paddingHorizontal: 48, paddingTop: 6, paddingBottom: 20 },
  // A4 leaves 515pt between the 40pt margins. Four items at the old minWidth of
  // 140 plus gaps needed 614pt, so the fourth wrapped onto its own line. They now
  // share the row equally instead of each demanding a fixed width.
  metaRow: { flexDirection: 'row', gap: 10 },
  metaItem: { flex: 1, minWidth: 0 },
  metaLabel: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 1,
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  // 10pt keeps the longest value ("Sep 8, 2026, 07:30 AM" is ~104pt here) inside
  // the ~121pt each item now gets, with room for longer locale formats.
  metaValue: { fontSize: 10, color: INK, fontWeight: 'bold' },
  sectionDivider: { marginHorizontal: 40, marginBottom: 16, borderBottom: `1pt solid ${HAIRLINE}` },
  body: { paddingHorizontal: 48 },
  // A prompt sits in open page flow, not on a card: in the workbook the coloured
  // callouts are the only panels, so carding every question competes with them.
  card: { marginBottom: 12, paddingTop: 4 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  qBadge: {
    backgroundColor: BRAND,
    color: '#fff',
    fontSize: 8,
    fontWeight: 'bold',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    letterSpacing: 0.6,
  },
  qType: {
    fontSize: 8,
    color: SUBTLE,
    marginLeft: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  qUnanswered: {
    fontSize: 8,
    fontWeight: 'bold',
    color: MUTED,
    marginLeft: 'auto',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: '#f0f0f0',
    letterSpacing: 0.6,
    // A flex child stretches to the row's height by default, so next to a prompt
    // that wraps onto two or three lines this pill grew into a tall grey slab.
    // Pinning it to the top keeps it pill-shaped whatever the prompt's height.
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  qText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: INK,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  qBody: {
    fontSize: 10,
    color: MUTED,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 6,
    marginTop: 4,
  },
  answer: { fontSize: 11, lineHeight: 1.5, color: BODY },
  answerEmpty: { fontSize: 11, fontStyle: 'italic', color: SUBTLE },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 4 },
  pillSelected: {
    backgroundColor: BRAND,
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pillTintSelected: {
    backgroundColor: BRAND_TINT,
    color: BRAND_DEEP,
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pillOff: {
    backgroundColor: PILL_OFF_BG,
    color: PILL_OFF_INK,
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    border: `1pt solid ${PILL_OFF_BORDER}`,
  },
  orderingItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  orderingNumber: { width: 18, fontSize: 10, color: BRAND, fontWeight: 'bold' },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  bulletDot: { width: 12, fontSize: 10, color: SUBTLE },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    border: `1pt solid ${HAIRLINE}`,
    borderRadius: 4,
    padding: 6,
    marginBottom: 4,
  },
  fileIconBox: {
    width: 28,
    height: 28,
    borderRadius: 3,
    backgroundColor: BRAND_TINT,
    color: BRAND_DEEP,
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    paddingTop: 9,
    marginRight: 8,
  },
  fileLink: {
    fontSize: 10,
    color: BRAND_DEEP,
    textDecoration: 'underline',
  },
  gradeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderBottom: `0.5pt solid ${HAIRLINE}`,
  },
  gradeLabel: { fontSize: 10, color: BODY, flex: 1, paddingRight: 10 },
  gradeValue: { fontSize: 10, fontWeight: 'bold', color: BRAND_DEEP },
  gradeValueEmpty: { fontSize: 10, color: SUBTLE },
  reflectionCard: {
    marginTop: 12,
    padding: 12,
    backgroundColor: BRAND_TINT,
    borderRadius: 6,
  },
  reflectionTitle: { fontSize: 11, fontWeight: 'bold', color: INK, marginBottom: 6 },
  reflectionItem: { flexDirection: 'row', marginBottom: 5 },
  reflectionNumber: { fontSize: 10, fontWeight: 'bold', color: BRAND, marginRight: 6 },
  reflectionText: { fontSize: 10, color: BODY, flex: 1, lineHeight: 1.5 },
  contentBlock: { marginBottom: 14, paddingHorizontal: 2 },
  // Images keep their aspect ratio inside the text column; `beside` splits that
  // column in two the way the on-screen block does on desktop.
  //
  // The height cap matters: without it react-pdf shrinks a portrait image to
  // whatever space is left at the bottom of the page, so a full-page diagram
  // lands as a thumbnail. Capped, it moves to the next page at a readable size.
  blockMedia: { width: '100%', maxHeight: PDF_IMAGE_MAX_HEIGHT, marginTop: 8, borderRadius: 6, objectFit: 'contain' },
  // Cells are narrow, so a cell image gets a much shorter cap than a block one —
  // a tall picture would otherwise stretch its row across most of a page.
  cellMedia: { width: '100%', maxHeight: 150, marginVertical: 3, objectFit: 'contain' },
  blockBesideRow: { flexDirection: 'row', gap: 12, alignItems: 'flex-start' },
  blockBesideCol: { flex: 1, minWidth: 0 },
  contentHeadline: { fontSize: 12, fontWeight: 'bold', color: INK, marginBottom: 4 },
  textBlock: {
    backgroundColor: '#fbfafa',
    borderLeft: `2pt solid ${BRAND}`,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 2,
    marginBottom: 2,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTop: `1pt solid ${HAIRLINE}`,
  },
  footerBrand: { fontSize: 8, color: MUTED, letterSpacing: 0.8 },
  footerPage: { fontSize: 8, color: MUTED },

  // ── Workbook layout ────────────────────────────────────────────────────────
  // The running header/footer and the cover reproduce the printed workbook, so a
  // member's download reads as the same document they worked through.
  runHeaderWrap: { position: 'absolute', top: 0, left: 0, right: 0 },
  runHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'baseline',
    paddingHorizontal: 48,
    paddingTop: 26,
    paddingBottom: 6,
  },
  runHeaderBrand: { fontSize: 8.5, fontWeight: 'bold', color: BRAND },
  runHeaderText: { fontSize: 8.5, color: MUTED },
  runHeaderRule: { marginHorizontal: 48, borderBottom: `0.75pt solid ${HAIRLINE}` },

  // The cover is the one full-bleed page: the brand artwork carries the top rule
  // and the logo, so the page itself takes no padding and nothing is drawn over
  // the artwork's edges.
  coverPage: { padding: 0, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  // A4 in points. `fixed` (on the element) keeps react-pdf from counting the
  // artwork towards the page's flow height, which otherwise pushes the whole
  // cover onto a second page; the size has to be explicit for the same reason.
  coverArt: { position: 'absolute', top: 0, left: 0, width: 595.28, height: 841.89 },
  // Vertical rhythm is set in absolute points against the A4 page rather than by
  // centring the block: the artwork's swoosh occupies the lower-left, so the
  // title has to sit in the upper third to stay clear of it.
  cover: { flexGrow: 1, paddingTop: 242, paddingHorizontal: 56, paddingBottom: 56 },
  // Narrower than the column so a long title stacks into short, balanced lines
  // rather than running the full width of the page.
  coverTitle: {
    fontSize: 36,
    fontWeight: 'bold',
    color: INK,
    textAlign: 'center',
    lineHeight: 1.2,
    maxWidth: 420,
    alignSelf: 'center',
  },
  coverKicker: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: BRAND,
    textAlign: 'center',
    letterSpacing: 1.1,
    marginTop: 30,
  },
  coverModule: {
    fontSize: 9,
    fontWeight: 'bold',
    color: INK,
    textAlign: 'center',
    letterSpacing: 1,
    marginTop: 20,
  },
  // A pill rather than a rule: it reads as a label on the workbook instead of a
  // second title, which is what the rules around it used to make it look like.
  coverBadgeRow: { flexDirection: 'row', justifyContent: 'center', marginTop: 84 },
  coverBadge: {
    backgroundColor: COVER_BADGE_BG,
    borderRadius: 13,
    paddingHorizontal: 18,
    paddingVertical: 7,
  },
  coverBadgeText: { fontSize: 11.5, color: BRAND, textAlign: 'center' },
  // Held narrower than the title block so the topic list breaks into the short,
  // centred lines the printed cover uses instead of one full-width run.
  coverTopics: {
    fontSize: 9,
    color: BODY,
    textAlign: 'center',
    marginTop: 14,
    lineHeight: 1.55,
    maxWidth: 320,
    alignSelf: 'center',
  },
  coverSpacer: { flexGrow: 1 },
  coverFields: { flexDirection: 'row', gap: 34, marginBottom: 22 },
  coverField: { flex: 1 },
  coverFieldValue: { fontSize: 10.5, color: INK, minHeight: 15 },
  coverFieldRule: { borderBottom: `0.75pt solid ${SUBTLE}`, marginTop: 2 },
  coverFieldLabel: { fontSize: 8, color: MUTED, marginTop: 4 },

  sectionHeading: { marginTop: 18, marginBottom: 10 },
  sectionHeadingTitle: { fontSize: 15, fontWeight: 'bold', color: BRAND_DEEP },
  sectionHeadingRule: { borderBottom: `1.2pt solid ${BRAND}`, marginTop: 5 },
  // Teacher feedback reads as a note from the teacher, not as another form: a
  // tinted panel with the brand bar, each prompt a quiet heading over plain prose.
  feedbackNote: { flexDirection: 'row', backgroundColor: FEEDBACK_TINT.bg, borderRadius: 4 },
  feedbackBar: { width: 3, backgroundColor: FEEDBACK_TINT.bar, borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
  feedbackPanel: { flex: 1, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 6 },
  feedbackFrom: { fontSize: 11, color: INK, marginBottom: 2 },
  feedbackFromName: { fontWeight: 'bold' },
  feedbackDate: { fontSize: 8.5, color: MUTED, marginBottom: 12 },
  feedbackItem: { marginBottom: 12 },
  feedbackPrompt: { fontSize: 10, fontWeight: 'bold', color: FEEDBACK_TINT.ink, marginBottom: 4 },
  // Grades inside the feedback note: the answers' grade rows, on the note's teal.
  feedbackGrades: {
    marginTop: 2,
    backgroundColor: '#ffffff',
    borderRadius: 4,
    paddingHorizontal: 10,
    border: `0.5pt solid ${FEEDBACK_TINT.border}`,
  },
  feedbackGradeRow: { borderBottom: `0.5pt solid ${FEEDBACK_TINT.border}` },
  feedbackGradeValue: { fontSize: 10, fontWeight: 'bold', color: FEEDBACK_TINT.ink },
  feedbackText: { fontSize: 11, color: BODY, lineHeight: 1.55 },
  feedbackPending: { fontSize: 10, color: MUTED, fontStyle: 'italic' },

  // A callout is a coloured panel with a solid accent bar down its left edge; the
  // bar is a sibling View because react-pdf has no per-side border radius.
  callout: { flexDirection: 'row', marginBottom: 12 },
  calloutBar: { width: 3 },
  calloutPanel: { flex: 1, paddingHorizontal: 12, paddingVertical: 10 },
  calloutLabel: { fontSize: 8, fontWeight: 'bold', letterSpacing: 0.8, marginBottom: 5 },

  prompt: { flexDirection: 'row', marginBottom: 6 },
  promptNumber: { width: 16, fontSize: 10.5, fontWeight: 'bold', color: BRAND },
  promptBody: { flex: 1 },
  responsePanel: {
    backgroundColor: '#fbfafa',
    borderLeft: `2pt solid ${BRAND_TINT}`,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 14,
  },
  responseLabel: { fontSize: 7.5, fontWeight: 'bold', letterSpacing: 0.8, color: SUBTLE, marginBottom: 4 },
  blockHeading: { fontSize: 12.5, fontWeight: 'bold', color: INK, marginBottom: 5, marginTop: 4 },
});

// Render one inline node (strong, em, a, br, etc.) for use inside a Text block
const renderInlineNode = (child: ChildNode, key: string): React.ReactNode => {
  if (child.nodeType === Node.TEXT_NODE) {
    return child.textContent ?? null;
  }
  if (child.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const c = child as Element;
  const tag = c.tagName.toLowerCase();
  const inner = renderInlineChildren(c, key);
  switch (tag) {
    // Bold is a weight, never a family. react-pdf registers Helvetica-Bold as a
    // family of its own holding only a normal face, so `fontFamily:
    // 'Helvetica-Bold'` here made a nested <em> ask for an italic face of that
    // family and throw "Could not resolve font for Helvetica-Bold, fontWeight
    // 400, fontStyle italic" — taking the whole download with it. Since this
    // helper gets no inherited-mark context, it cannot resolve the pair itself;
    // keeping the family Helvetica and varying weight and style lets react-pdf
    // compose <strong><em> into Helvetica-BoldOblique on its own.
    case 'strong':
    case 'b': {
      return (
        <Text key={key} style={{ fontWeight: 'bold' }}>
          {inner}
        </Text>
      );
    }
    case 'em':
    case 'i': {
      return (
        <Text key={key} style={{ fontStyle: 'italic' }}>
          {inner}
        </Text>
      );
    }
    case 'u': {
      return (
        <Text key={key} style={{ textDecoration: 'underline' }}>
          {inner}
        </Text>
      );
    }
    case 'a': {
      const href = c.getAttribute('href') ?? '';
      const text = c.textContent ?? '';
      if (href.startsWith('http://') || href.startsWith('https://')) {
        return (
          <Link key={key} src={href} style={{ color: BRAND_DEEP, textDecoration: 'underline', fontSize: 10 }}>
            {text}
          </Link>
        );
      }
      return (
        <Text key={key} style={{ color: BRAND_DEEP, textDecoration: 'underline' }}>
          {text}
        </Text>
      );
    }
    case 'br': {
      return '\n';
    }
    default: {
      return <Text key={key}>{inner}</Text>;
    }
  }
};

// Render inline children (strong, em, a, br, etc.) inside a Text block
const renderInlineChildren = (el: Element, keyPrefix: string): React.ReactNode[] =>
  [...el.childNodes].map((child, i) => renderInlineNode(child, `${keyPrefix}-${i}`));

/**
 * A table cell's contents, as siblings rather than one `Text`.
 *
 * react-pdf will not render an `Image` nested inside a `Text`, so a cell holding
 * a picture has to break its inline runs around it. Cells without an image take
 * the single-`Text` path unchanged.
 */
const renderCellContent = (
  el: Element,
  keyPrefix: string,
  textStyle: Style,
  images: TPdfImageMap | undefined,
): React.ReactNode => {
  const inlineOnly = !el.querySelector('img');
  if (inlineOnly) {
    return <Text style={textStyle}>{renderInlineChildren(el, keyPrefix)}</Text>;
  }

  const out: React.ReactNode[] = [];
  let run: React.ReactNode[] = [];
  let index = 0;

  const flushRun = () => {
    // A run of whitespace-only nodes is not worth a Text of its own — it would
    // add a blank line above or below the picture.
    if (run.some(node => typeof node !== 'string' || node.trim().length > 0)) {
      out.push(
        <Text key={`${keyPrefix}-t-${index++}`} style={textStyle}>
          {run}
        </Text>,
      );
    }
    run = [];
  };

  const walk = (node: ChildNode) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      if (element.tagName.toLowerCase() === 'img') {
        const resolved = images?.get(element.getAttribute('src') ?? '');
        if (resolved) {
          flushRun();
          out.push(<Image key={`${keyPrefix}-i-${index++}`} src={resolved} style={styles.cellMedia} />);
        }
        return;
      }
      // A wrapper around the image (TipTap nests content in <p>) is descended
      // into so the picture is reached; anything else stays an inline run.
      if (element.querySelector('img')) {
        [...element.childNodes].forEach(walk);
        return;
      }
    }
    run.push(renderInlineNode(node, `${keyPrefix}-n-${index++}`));
  };

  [...el.childNodes].forEach(walk);
  flushRun();
  return out;
};

interface TableCellPlacement {
  el: Element;
  rowIndex: number;
  colIndex: number;
  colspan: number;
  rowspan: number;
}

const TABLE_CELL_TAGS = new Set(['td', 'th']);

/**
 * An authored HTML table as a flex grid — react-pdf has no table primitive, so
 * every row is a flex row and every cell a column of proportional width.
 *
 * Cells are laid out on a GRID that honours `rowspan`/`colspan` rather than
 * emitting each `<tr>`'s children in order. A row sitting under a vertical span
 * carries fewer `<td>`s than the table has columns, so the naive version started
 * it at column 0 and shifted every value one column left — which is how the
 * video-outlines table put chapter names under "Link to the Video" and left the
 * timecode column empty.
 *
 * Column widths are split evenly rather than honouring the editor's `<colgroup>`:
 * the page is a fixed width the author never saw, and scaled pixel widths collapse
 * a narrow column to nothing. Cells render inline content only — a nested block
 * (a list inside a cell) flattens to its text, the honest limit of a paper grid.
 */
const renderTableToPdf = (table: Element, keyPrefix: string, images?: TPdfImageMap): React.ReactNode[] => {
  const rows = [...table.querySelectorAll('tr')];
  if (rows.length === 0) {
    return [];
  }

  const occupied: boolean[][] = rows.map(() => []);
  const placements: TableCellPlacement[] = [];
  let columnCount = 0;

  rows.forEach((row, rowIndex) => {
    let colIndex = 0;
    for (const child of row.children) {
      if (!TABLE_CELL_TAGS.has(child.tagName.toLowerCase())) {
        continue;
      }
      while (occupied[rowIndex][colIndex]) {
        colIndex++;
      }
      const colspan = Math.max(1, Number(child.getAttribute('colspan')) || 1);
      const rowspan = Math.max(1, Number(child.getAttribute('rowspan')) || 1);
      const lastRow = Math.min(rows.length, rowIndex + rowspan);
      for (let r = rowIndex; r < lastRow; r++) {
        for (let c = colIndex; c < colIndex + colspan; c++) {
          occupied[r][c] = true;
        }
      }
      placements.push({ el: child, rowIndex, colIndex, colspan, rowspan });
      colIndex += colspan;
      columnCount = Math.max(columnCount, colIndex);
    }
  });

  if (columnCount === 0) {
    return [];
  }

  // Which placement covers each grid position, so a continuation row can draw the
  // right-hand edge of a merged cell without repeating its content.
  const coverAt = new Map<string, TableCellPlacement>();
  for (const placement of placements) {
    const lastRow = Math.min(rows.length, placement.rowIndex + placement.rowspan);
    for (let r = placement.rowIndex; r < lastRow; r++) {
      for (let c = placement.colIndex; c < placement.colIndex + placement.colspan; c++) {
        coverAt.set(`${r}:${c}`, placement);
      }
    }
  }

  const lastRowIndex = rows.length - 1;

  return rows.map((_row, rowIndex) => {
    const cells: React.ReactNode[] = [];
    let colIndex = 0;

    while (colIndex < columnCount) {
      const cover = coverAt.get(`${rowIndex}:${colIndex}`);
      const cellKey = `${keyPrefix}-r-${rowIndex}-c-${colIndex}`;

      if (!cover) {
        // Ragged row — fill the gap so the columns after it stay aligned.
        cells.push(
          <View
            key={cellKey}
            style={{
              width: `${100 / columnCount}%`,
              borderTop: rowIndex === 0 ? `1pt solid ${HAIRLINE}` : undefined,
              borderLeft: colIndex === 0 ? `1pt solid ${HAIRLINE}` : undefined,
              borderRight: `1pt solid ${HAIRLINE}`,
              borderBottom: `1pt solid ${HAIRLINE}`,
            }}
          />,
        );
        colIndex += 1;
        continue;
      }

      const isOrigin = cover.rowIndex === rowIndex;
      const isHeader = cover.el.tagName.toLowerCase() === 'th';
      const isLastRowOfSpan = rowIndex === Math.min(rows.length, cover.rowIndex + cover.rowspan) - 1;

      cells.push(
        <View
          key={cellKey}
          style={{
            width: `${(100 * cover.colspan) / columnCount}%`,
            padding: 5,
            // The table draws no frame of its own (its rows are siblings in the
            // page flow), so the outer edges live on the cells.
            borderTop: rowIndex === 0 ? `1pt solid ${isHeader ? BRAND : HAIRLINE}` : undefined,
            borderLeft: colIndex === 0 ? `1pt solid ${isHeader ? BRAND : HAIRLINE}` : undefined,
            borderRight: `1pt solid ${isHeader ? BRAND : HAIRLINE}`,
            // Only the last row a span covers draws the bottom edge, so a merged
            // cell reads as one tall cell instead of a stack of empty boxes.
            borderBottom: isLastRowOfSpan ? `1pt solid ${isHeader ? BRAND : HAIRLINE}` : undefined,
            // Zebra keyed on the cell's OWN first row, so a merged cell keeps one
            // colour down its whole height.
            backgroundColor: isHeader ? BRAND : cover.rowIndex % 2 === 0 ? '#ffffff' : CARD,
          }}
        >
          {isOrigin
            ? renderCellContent(
                cover.el,
                cellKey,
                {
                  fontSize: 9,
                  lineHeight: 1.4,
                  color: isHeader ? '#ffffff' : BODY,
                  fontFamily: 'Helvetica',
                  fontWeight: isHeader ? 'bold' : 'normal',
                },
                images,
              )
            : null}
        </View>,
      );
      colIndex += cover.colspan;
    }

    return (
      // The row is the unbreakable unit: an unbreakable block taller than the page
      // makes react-pdf overlap everything around it, so only rows are atomic.
      <View
        // oxlint-disable-next-line react/no-array-index-key
        key={`${keyPrefix}-r-${rowIndex}`}
        style={{
          flexDirection: 'row',
          marginTop: rowIndex === 0 ? 6 : 0,
          marginBottom: rowIndex === lastRowIndex ? 6 : 0,
        }}
        wrap={false}
      >
        {cells}
      </View>
    );
  });
};

type HtmlRenderContext = 'body' | 'title';

/**
 * Tags that carry their own block layout. Used to decide whether a container
 * (a `div`, or a `p` holding pasted markup) should be recursed into as blocks or
 * flattened to a single line of inline text.
 */
const BLOCK_TAGS = new Set([
  'address',
  'article',
  'blockquote',
  'div',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'iframe',
  'img',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'ul',
]);

const hasBlockChild = (el: Element): boolean =>
  [...el.children].some(child => BLOCK_TAGS.has(child.tagName.toLowerCase()));

/**
 * Convert an HTML string to react-pdf nodes.
 *
 * The walk is RECURSIVE, not a single pass over `body.childNodes`: authored HTML
 * nests (TipTap wraps a table in a div, pasted Google Docs markup nests divs
 * several deep), and a flat walk sent those containers through the inline
 * renderer — which collapsed an entire table into one run-together paragraph.
 * A container is recursed into when it holds block children and only rendered as
 * inline text when it genuinely has none.
 */
const renderHtmlToPdf = (
  html: string | null | undefined,
  context: HtmlRenderContext = 'body',
  images?: TPdfImageMap,
): React.ReactNode => {
  if (!html?.trim() || typeof window === 'undefined') {
    return null;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let idx = 0;

  const isTitle = context === 'title';
  const bodyStyle = isTitle
    ? ({ fontSize: 11, color: INK, lineHeight: 1.4, marginBottom: 3 } as const)
    : ({ fontSize: 10, color: MUTED, lineHeight: 1.5, marginBottom: 3 } as const);
  const headingStyle = isTitle
    ? ({ fontSize: 13, fontWeight: 'bold', color: INK, lineHeight: 1.4, marginBottom: 4 } as const)
    : ({
        fontSize: 11,
        fontWeight: 'bold',
        color: INK,
        lineHeight: 1.4,
        marginBottom: 4,
      } as const);

  const renderNodes = (nodes: ChildNode[]): React.ReactNode[] => nodes.flatMap(node => renderNode(node));

  const renderNode = (node: ChildNode): React.ReactNode[] => {
    const key = `h-${idx++}`;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text
        ? [
            <Text key={key} style={bodyStyle}>
              {text}
            </Text>,
          ]
        : [];
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return [];
    }

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      return [
        <Text key={key} style={headingStyle}>
          {renderInlineChildren(el, key)}
        </Text>,
      ];
    }

    if (tag === 'ul' || tag === 'ol') {
      let li = 0;
      return [...el.children].flatMap(child => {
        if (child.tagName.toLowerCase() !== 'li') {
          return [];
        }
        li++;
        const liKey = `${key}-li-${li}`;
        return [
          <View key={liKey} style={{ flexDirection: 'row', marginBottom: 2, paddingLeft: 8 }}>
            <Text style={{ width: 14, fontSize: 10, color: MUTED }}>{tag === 'ol' ? `${li}.` : '\u2022'}</Text>
            <Text style={{ flex: 1, fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
              {renderInlineChildren(child, liKey)}
            </Text>
          </View>,
        ];
      });
    }

    if (tag === 'table') {
      return renderTableToPdf(el, key, images);
    }

    if (tag === 'img') {
      // Only ever the preloaded data URL. Resolving the src here instead would
      // make react-pdf fetch it mid-render, which fails on the WebP the media
      // manager stores and throws on a bad src, taking the whole PDF with it.
      const src = el.getAttribute('src') ?? '';
      const resolved = images?.get(src);
      return resolved
        ? [
            <Image
              key={key}
              src={resolved}
              style={{ width: '100%', maxHeight: PDF_IMAGE_MAX_HEIGHT, marginVertical: 6, objectFit: 'contain' }}
            />,
          ]
        : [];
    }

    if (tag === 'iframe') {
      // A video can't play on paper — print a link to it so the reader can still
      // reach the thing the author embedded.
      const src = el.getAttribute('src') ?? '';
      return src.startsWith('http')
        ? [
            <Link key={key} src={src} style={{ ...bodyStyle, color: BRAND_DEEP, textDecoration: 'underline' }}>
              Watch the video
            </Link>,
          ]
        : [];
    }

    if (tag === 'hr') {
      return [<View key={key} style={{ borderBottom: `1pt solid ${HAIRLINE}`, marginVertical: 8 }} />];
    }

    if (tag === 'br') {
      return [
        <Text key={key} style={bodyStyle}>
          {'\n'}
        </Text>,
      ];
    }

    // Containers and anything unrecognised: recurse when there is block content
    // inside, otherwise render the element's inline content as one paragraph.
    if (hasBlockChild(el)) {
      return renderNodes([...el.childNodes]);
    }

    return [
      <Text key={key} style={bodyStyle}>
        {renderInlineChildren(el, key)}
      </Text>,
    ];
  };

  return <>{renderNodes([...doc.body.childNodes])}</>;
};

const stripHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Date only — the cover's "Date started" rule carries no time in the workbook. */
const formatDay = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').filter(Boolean).pop() || url;
    return decodeURIComponent(last.replace(/^\d+-/, ''));
  } catch {
    return url.split('/').pop() || url;
  }
};

/**
 * Whether a question carries an answer.
 *
 * The per-question "NOT ANSWERED" chip and the header's "Answered n / m" tally
 * both need this, and they previously each had their own copy — which is how
 * multiple_grading ended up counted as unanswered in the tally while the chip
 * said otherwise. One definition, so they cannot disagree again.
 */
const isAnswerPresent = (question: TFormQuestion, answer: TReviewAnswer | undefined): boolean => {
  if (!answer) {
    return false;
  }
  const textAnswer = answer.textAnswer?.trim() ?? '';

  switch (question.questionType) {
    case 'short_answer':
    case 'dropdown': {
      return textAnswer.length > 0;
    }
    case 'ordering': {
      return (answer.orderingSequence?.length ?? 0) > 0;
    }
    case 'file_upload': {
      return (answer.fileUrls?.length ?? 0) > 0;
    }
    case 'accept': {
      return textAnswer === 'accepted';
    }
    case 'accept_with_signature': {
      try {
        const parsed = JSON.parse(textAnswer || '{}') as { checked?: boolean; name?: string };
        return parsed.checked === true && (parsed.name?.trim().split(/\s+/).filter(Boolean).length ?? 0) >= 2;
      } catch {
        return false;
      }
    }
    case 'multiple_grading': {
      const rows = getGradingRows(question.metadata);
      const grades = getAnswerGrades(answer.value);
      const optedOut = new Set(getAnswerOptedOut(answer.value));
      return rows.length > 0 && rows.every(row => typeof grades[row.id] === 'number' || optedOut.has(row.id));
    }
    default: {
      return (answer.selectedOptionIds?.length ?? 0) > 0;
    }
  }
};

/**
 * Keeping a content block whole across a page break.
 *
 * react-pdf breaks a block wherever it runs out of page, which routinely strands
 * a single bullet of a callout at the top of the next page, cut off from the
 * heading and the sentences it belongs to. A block small enough to sit on a page
 * of its own is marked unbreakable instead, so it moves across whole.
 *
 * Only small ones: an unbreakable block taller than a page makes react-pdf
 * overlap it with everything around it, which is far worse than an ugly break.
 * Since the height here is ESTIMATED from the text rather than measured, the
 * threshold is set at under half the page — even an estimate wrong by a factor
 * of two still describes something that fits on a fresh page.
 */
const PAGE_CONTENT_HEIGHT = 842 - 56 - 56;
const BLOCK_KEEP_WHOLE_MAX_HEIGHT = PAGE_CONTENT_HEIGHT * 0.45;

/**
 * A block this short is a lead-in — a heading, at most a line of setup — rather
 * than content standing on its own, so it is held back to travel with the block
 * it introduces. Without this, moving a callout across a break strands the
 * heading above it: a different orphan, no better than the old one.
 *
 * Kept below the height of even a one-line callout (whose padding and label come
 * to 71pt before any prose) and applied to nothing else. Reserving the next
 * block's height is a hard claim on the foot of the page, so when ordinary
 * content qualified as a lead-in every block in a run reserved the one after it
 * and the reservations compounded, emptying the bottom third of the page.
 */
const LEAD_IN_MAX_HEIGHT = 60;

/** Room a section title claims below itself: its rule plus a prompt's first lines. */
const SECTION_HEADING_PRESENCE = 72;

// Line metrics matching the styles the block renderer actually uses.
const BODY_LINE_HEIGHT = 15; // fontSize 10 * lineHeight 1.5
const HEADING_LINE_HEIGHT = 16;
const ELEMENT_MARGIN = 3;
const LIST_ITEM_MARGIN = 2;
const TABLE_ROW_HEIGHT = 26;

// Characters per line at 10pt Helvetica across each measure. Deliberately low:
// underestimating the line count is what could mark a tall block unbreakable.
const CALLOUT_CHARS_PER_LINE = 80;
const BLOCK_CHARS_PER_LINE = 88;
const LIST_INDENT_CHARS = 5;

const estimateLines = (text: string, charsPerLine: number): number =>
  Math.max(1, Math.ceil(text.trim().length / charsPerLine));

/** Rough print height of an authored HTML fragment, in points. */
const estimateHtmlHeight = (html: string | null | undefined, charsPerLine: number): number => {
  if (!html?.trim() || typeof window === 'undefined') {
    return 0;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let height = 0;

  const walk = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        height += estimateLines(text, charsPerLine) * BODY_LINE_HEIGHT + ELEMENT_MARGIN;
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      height += estimateLines(el.textContent ?? '', charsPerLine) * HEADING_LINE_HEIGHT + 4;
      return;
    }
    if (tag === 'ul' || tag === 'ol') {
      for (const item of el.children) {
        if (item.tagName.toLowerCase() === 'li') {
          height +=
            estimateLines(item.textContent ?? '', charsPerLine - LIST_INDENT_CHARS) * BODY_LINE_HEIGHT +
            LIST_ITEM_MARGIN;
        }
      }
      return;
    }
    if (tag === 'table') {
      height += Math.max(1, el.querySelectorAll('tr').length) * TABLE_ROW_HEIGHT;
      return;
    }
    if (tag === 'img') {
      // An image's real height is not known here, so it is assumed to be the cap.
      // That puts any block carrying one over the threshold, which is the safe
      // answer: it keeps today's breaking behaviour rather than risking overlap.
      height += PDF_IMAGE_MAX_HEIGHT;
      return;
    }
    if (tag === 'iframe') {
      height += BODY_LINE_HEIGHT + ELEMENT_MARGIN; // printed as a single link line
      return;
    }
    if (tag === 'hr') {
      height += BODY_LINE_HEIGHT + ELEMENT_MARGIN;
      return;
    }
    if (tag === 'br') {
      height += BODY_LINE_HEIGHT;
      return;
    }
    if (hasBlockChild(el)) {
      [...el.childNodes].forEach(walk);
      return;
    }
    height += estimateLines(el.textContent ?? '', charsPerLine) * BODY_LINE_HEIGHT + ELEMENT_MARGIN;
  };

  [...doc.body.childNodes].forEach(walk);
  return height;
};

/** Rough print height of a whole text block, in points. */
const estimateTextBlockHeight = ({
  body,
  headline,
  isCallout,
  hasImage,
}: {
  body: string | null | undefined;
  headline: string;
  isCallout: boolean;
  hasImage: boolean;
}): number => {
  const charsPerLine = isCallout ? CALLOUT_CHARS_PER_LINE : BLOCK_CHARS_PER_LINE;
  // Panel padding and the block's own bottom margin, plus the label or heading.
  const chrome = (isCallout ? 20 + 12 : 12) + (headline ? HEADING_LINE_HEIGHT + 5 : 0);
  // An image's real height is unknown here, so it counts as the cap — which puts
  // the block over the threshold and leaves its breaking behaviour untouched.
  return estimateHtmlHeight(body, charsPerLine) + chrome + (hasImage ? PDF_IMAGE_MAX_HEIGHT : 0);
};

// Question-card metrics, matching the styles the card is actually built from.
const PROMPT_CHARS_PER_LINE = 66; // 12pt bold across the prompt column
const PROMPT_LINE_HEIGHT = 17;
const ANSWER_CHARS_PER_LINE = 78; // 11pt
const ANSWER_LINE_HEIGHT = 17;
const SECTION_LABEL_HEIGHT = 19;
const ANSWER_PANEL_CHROME = 20;
const PILL_ROW_HEIGHT = 26;
const ORDERING_ITEM_HEIGHT = 20;
const FILE_ITEM_HEIGHT = 44;
const GRADE_ROW_HEIGHT = 24;
const CARD_CHROME = 16; // paddingTop plus the card's bottom margin
const ANSWER_COLUMN_WIDTH = 483;

/**
 * Rough print height of a question and its answer, in points.
 *
 * Same purpose as the text-block estimate: a card small enough to sit on a page
 * of its own is kept whole, so a prompt is never printed with its answer on the
 * following page. Deliberately generous — overestimating only means the card
 * keeps the breaking behaviour it has today.
 */
const estimateQuestionHeight = ({
  question,
  hasAnswer,
  textAnswer,
  optionTexts,
  fileCount,
}: {
  question: TFormQuestion;
  hasAnswer: boolean;
  textAnswer: string;
  optionTexts: string[];
  fileCount: number;
}): number => {
  const promptHtml = /<[^>]+>/.test(question.questionText);
  let height =
    CARD_CHROME +
    (promptHtml
      ? estimateHtmlHeight(question.questionText, PROMPT_CHARS_PER_LINE)
      : estimateLines(question.questionText, PROMPT_CHARS_PER_LINE) * PROMPT_LINE_HEIGHT + 4) +
    estimateHtmlHeight(question.questionBody, BLOCK_CHARS_PER_LINE) +
    6; // prompt row margin

  /** A pill row wraps, so the pills are measured and divided by the column. */
  const pillRows = (texts: string[]): number => {
    const total = texts.reduce((sum, text) => sum + text.length * 5 + 26, 0);
    return Math.max(1, Math.ceil(total / ANSWER_COLUMN_WIDTH));
  };
  const answerPanel = (text: string): number =>
    estimateLines(text, ANSWER_CHARS_PER_LINE) * ANSWER_LINE_HEIGHT + ANSWER_PANEL_CHROME;

  switch (question.questionType) {
    case 'multiple_grading': {
      height += SECTION_LABEL_HEIGHT + getGradingRows(question.metadata).length * GRADE_ROW_HEIGHT;
      const reflection = getGradingReflection(question.metadata);
      if (reflection) {
        height += 12 + 24 + 17 + reflection.prompts.length * 20;
      }
      break;
    }
    case 'file_upload': {
      height += SECTION_LABEL_HEIGHT + (hasAnswer ? fileCount * FILE_ITEM_HEIGHT : ANSWER_LINE_HEIGHT);
      break;
    }
    case 'ordering': {
      height += SECTION_LABEL_HEIGHT + optionTexts.length * ORDERING_ITEM_HEIGHT;
      if (!hasAnswer) {
        height += ANSWER_LINE_HEIGHT + 4;
      }
      break;
    }
    case 'short_answer':
    case 'dropdown':
    case 'accept_with_signature': {
      height += SECTION_LABEL_HEIGHT + (hasAnswer ? answerPanel(textAnswer) : ANSWER_LINE_HEIGHT);
      break;
    }
    case 'accept': {
      height += SECTION_LABEL_HEIGHT + (hasAnswer ? PILL_ROW_HEIGHT : ANSWER_LINE_HEIGHT);
      break;
    }
    default: {
      // Choice types print every option as a pill, then the answer beneath.
      if (optionTexts.length > 0) {
        height += SECTION_LABEL_HEIGHT + pillRows(optionTexts) * PILL_ROW_HEIGHT;
        if (!hasAnswer) {
          height += SECTION_LABEL_HEIGHT + ANSWER_LINE_HEIGHT;
        }
      }
      break;
    }
  }

  return height;
};

interface QuestionPdfBlockProps {
  index: number;
  question: TFormQuestion;
  answer: TReviewAnswer | undefined;
  images?: TPdfImageMap;
}

const QuestionPdfBlock = ({ index, question, answer, images }: QuestionPdfBlockProps) => {
  if (question.questionType === 'text_block') {
    const blocks = getTextBlocks(question.metadata);
    if (blocks.length === 0) {
      return null;
    }

    // Measured up front rather than per block, because a block's page-break
    // behaviour depends on the one after it as well as on itself.
    const plans = blocks.map(block => {
      const tint = getFormBlockTint(block.background);
      const isCallout = block.background !== EFormBlockBackground.NONE;
      const headline = block.headline || tint.defaultHeadline;
      const blockImage = block.media?.url ? images?.get(block.media.url) : undefined;
      const height = estimateTextBlockHeight({
        body: block.body,
        headline,
        isCallout,
        hasImage: !!blockImage,
      });
      return {
        block,
        tint,
        isCallout,
        headline,
        hasBar: tint.bar !== 'transparent',
        blockImage,
        height,
        keepWhole: height <= BLOCK_KEEP_WHOLE_MAX_HEIGHT,
      };
    });

    return (
      <View style={styles.contentBlock}>
        {plans.map((plan, index) => {
          const { block, tint, isCallout, headline, hasBar, blockImage, keepWhole } = plan;

          // Reserve the next block's height when this one is only a lead-in, so
          // the pair breaks together instead of apart.
          const next = plans[index + 1];
          const minPresenceAhead = plan.height <= LEAD_IN_MAX_HEIGHT && next?.keepWhole ? next.height : undefined;

          // `beside` only holds up when there is prose to sit next to; an image on
          // its own gets the full measure either way.
          const isBeside = !!blockImage && block.mediaLayout === EFormBlockMediaLayout.BESIDE && !!block.body?.trim();
          const body = block.body ? renderHtmlToPdf(block.body, 'body', images) : null;
          const media = blockImage ? <Image src={blockImage} style={styles.blockMedia} /> : null;

          const content = isBeside ? (
            <View style={styles.blockBesideRow}>
              <View style={styles.blockBesideCol}>{body}</View>
              <View style={styles.blockBesideCol}>{media}</View>
            </View>
          ) : (
            <>
              {body}
              {media}
            </>
          );

          // A named callout ("Pause & Reflect") is drawn the way the workbook draws
          // it — accent bar, small coloured label, tinted panel. The tint carries the
          // callout's meaning, so it has to survive into the PDF or Key Term and In
          // Your Practice become indistinguishable.
          if (isCallout && hasBar) {
            return (
              <View
                key={block.id}
                wrap={!keepWhole}
                minPresenceAhead={minPresenceAhead}
                style={[styles.callout, { backgroundColor: tint.bg }]}
              >
                <View style={[styles.calloutBar, { backgroundColor: tint.bar }]} />
                <View style={styles.calloutPanel}>
                  {headline ? (
                    <Text style={[styles.calloutLabel, { color: tint.ink }]}>{headline.toUpperCase()}</Text>
                  ) : null}
                  {content}
                </View>
              </View>
            );
          }

          return (
            <View
              key={block.id}
              wrap={!keepWhole}
              minPresenceAhead={minPresenceAhead}
              style={
                isCallout
                  ? { marginBottom: 12, padding: 10, borderRadius: 6, backgroundColor: tint.bg }
                  : { marginBottom: 12 }
              }
            >
              {headline ? <Text style={styles.blockHeading}>{headline}</Text> : null}
              {content}
            </View>
          );
        })}
      </View>
    );
  }

  const sortedOptions = [...question.options].sort((a, b) => a.position - b.position);
  const selectedIds = new Set(answer?.selectedOptionIds ?? []);
  const orderingIds = answer?.orderingSequence ?? [];
  const textAnswer = answer?.textAnswer?.trim() ?? '';
  const fileUrls = answer?.fileUrls ?? [];
  const grades = getAnswerGrades(answer?.value);
  const optedOut = new Set(getAnswerOptedOut(answer?.value));

  const hasAnswer = isAnswerPresent(question, answer);

  const reflection = getGradingReflection(question.metadata);

  const questionTextIsHtml = /<[^>]+>/.test(question.questionText);

  // A prompt printed at the foot of one page with its answer on the next reads as
  // two unrelated fragments, so a card small enough to sit on a page of its own
  // moves across whole.
  const keepQuestionWhole =
    estimateQuestionHeight({
      question,
      hasAnswer,
      textAnswer,
      optionTexts: sortedOptions.map(option => option.optionText),
      fileCount: fileUrls.length,
    }) <= BLOCK_KEEP_WHOLE_MAX_HEIGHT;

  return (
    <View style={styles.card} wrap={!keepQuestionWhole}>
      {/* The workbook numbers its prompts in brand pink rather than badging them
          with a type chip — the type is obvious from the answer beneath it. */}
      <View style={styles.prompt}>
        <Text style={styles.promptNumber}>{index + 1}</Text>
        <View style={styles.promptBody}>
          {questionTextIsHtml ? (
            renderHtmlToPdf(question.questionText, 'title', images)
          ) : (
            <Text style={styles.qText}>{question.questionText}</Text>
          )}
          {question.questionBody ? (
            <View style={{ marginTop: 2 }}>{renderHtmlToPdf(question.questionBody, 'body', images)}</View>
          ) : null}
        </View>
        {!hasAnswer && <Text style={styles.qUnanswered}>NOT ANSWERED</Text>}
      </View>

      {/* File upload — list uploaded files as clickable links */}
      {question.questionType === 'file_upload' && (
        <>
          <Text style={styles.sectionLabel}>Uploaded Files</Text>
          {hasAnswer ? (
            <View>
              {fileUrls.map(url => {
                const ext = (url.split('?')[0].split('.').pop() || 'FILE').toUpperCase().slice(0, 4);
                return (
                  <View key={url} style={styles.fileItem}>
                    <Text style={styles.fileIconBox}>{ext}</Text>
                    <Link src={url} style={styles.fileLink}>
                      {fileNameFromUrl(url)}
                    </Link>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No files uploaded</Text>
          )}
        </>
      )}

      {/* Grading — one row per graded item with its score out of the scale max */}
      {question.questionType === 'multiple_grading' && (
        <>
          <Text style={styles.sectionLabel}>Your Ratings</Text>
          {getGradingRows(question.metadata).length > 0 ? (
            <View>
              {getGradingRows(question.metadata).map(row => {
                const grade = grades[row.id];
                const isOptedOut = optedOut.has(row.id);
                const graded = typeof grade === 'number';
                return (
                  <View key={row.id} style={styles.gradeRow}>
                    <Text style={styles.gradeLabel}>{row.label}</Text>
                    <Text style={graded || isOptedOut ? styles.gradeValue : styles.gradeValueEmpty}>
                      {graded
                        ? `${grade} / ${getGradingScale(question.metadata).max}`
                        : isOptedOut
                          ? getGradingOptOut(question.metadata).label
                          : '—'}
                    </Text>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No rows configured</Text>
          )}
        </>
      )}

      {question.questionType === 'multiple_grading' && reflection && (
        <View style={styles.reflectionCard}>
          <Text style={styles.reflectionTitle}>{reflection.title}</Text>
          {reflection.prompts.map((prompt, promptIndex) => (
            // Prompts are a fixed ordered list of strings with no id; this tree is
            // rendered once into a PDF, so there is no reconciliation to protect.
            // oxlint-disable-next-line react/no-array-index-key
            <View key={`${question.id}-prompt-${promptIndex}`} style={styles.reflectionItem}>
              <Text style={styles.reflectionNumber}>{String(promptIndex + 1).padStart(2, '0')}.</Text>
              <Text style={styles.reflectionText}>{prompt}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Options panel — always show all available choices, selected ones highlighted */}
      {question.questionType !== 'short_answer' &&
        question.questionType !== 'file_upload' &&
        question.questionType !== 'ordering' &&
        question.questionType !== 'accept' &&
        question.questionType !== 'accept_with_signature' &&
        question.questionType !== 'dropdown' &&
        question.questionType !== 'multiple_grading' &&
        sortedOptions.length > 0 && (
          <>
            <Text style={styles.sectionLabel}>Options</Text>
            <View style={styles.pillRow}>
              {sortedOptions.map(opt => (
                <Text key={opt.id} style={selectedIds.has(opt.id) ? styles.pillSelected : styles.pillOff}>
                  {selectedIds.has(opt.id) ? '✓ ' : ''}
                  {opt.optionText}
                </Text>
              ))}
            </View>
            {!hasAnswer && (
              <>
                <Text style={styles.sectionLabel}>Your Answer</Text>
                <Text style={styles.answerEmpty}>No answer provided</Text>
              </>
            )}
          </>
        )}

      {/* Short answer — text the user typed */}
      {question.questionType === 'short_answer' && (
        <>
          <Text style={styles.sectionLabel}>Your Answer</Text>
          {hasAnswer ? (
            <View style={styles.textBlock}>
              <Text style={styles.answer}>{textAnswer}</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Ordering — show user's sequence; falls back to listing the options */}
      {question.questionType === 'ordering' && (
        <>
          <Text style={styles.sectionLabel}>{hasAnswer ? 'Your Order' : 'Options'}</Text>
          {hasAnswer ? (
            <View>
              {orderingIds
                .map(id => sortedOptions.find(o => o.id === id))
                .filter((o): o is (typeof sortedOptions)[number] => !!o)
                .map((opt, i) => (
                  <View key={opt.id} style={styles.orderingItem}>
                    <Text style={styles.orderingNumber}>{i + 1}.</Text>
                    <Text style={styles.answer}>{opt.optionText}</Text>
                  </View>
                ))}
            </View>
          ) : (
            <>
              <View>
                {sortedOptions.map(opt => (
                  <View key={opt.id} style={styles.bulletItem}>
                    <Text style={styles.bulletDot}>• </Text>
                    <Text style={styles.answer}>{opt.optionText}</Text>
                  </View>
                ))}
              </View>
              <Text style={[styles.answerEmpty, { marginTop: 4 }]}>No order submitted</Text>
            </>
          )}
        </>
      )}

      {/* Accept — show accepted state as a pill */}
      {question.questionType === 'accept' && (
        <>
          <Text style={styles.sectionLabel}>Your Answer</Text>
          {hasAnswer ? (
            <View style={styles.pillRow}>
              <Text style={styles.pillSelected}>✓ Accepted</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Accept with signature — show checkbox + name */}
      {question.questionType === 'accept_with_signature' && (
        <>
          <Text style={styles.sectionLabel}>Signature</Text>
          {hasAnswer ? (
            (() => {
              try {
                const parsed = JSON.parse(textAnswer) as { checked?: boolean; name?: string };
                return (
                  <View style={styles.textBlock}>
                    <Text style={styles.answer}>✓ Acknowledged · Signed: {parsed.name?.trim() ?? ''}</Text>
                  </View>
                );
              } catch {
                return <Text style={styles.answerEmpty}>No answer provided</Text>;
              }
            })()
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Dropdown — show selected value */}
      {question.questionType === 'dropdown' && (
        <>
          <Text style={styles.sectionLabel}>Your Selection</Text>
          {hasAnswer ? (
            <View style={styles.textBlock}>
              <Text style={styles.answer}>{textAnswer}</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}
    </View>
  );
};

/**
 * The running header and footer that sit on every page of the workbook. Both are
 * `fixed`, so react-pdf repeats them rather than laying them out once.
 */
const RunningHeader = ({ formTitle }: { formTitle: string }) => (
  <View style={styles.runHeaderWrap} fixed>
    <View style={styles.runHeader}>
      <Text style={styles.runHeaderBrand}>Restore Your Core®</Text>
      <Text style={styles.runHeaderText}>{`  ·  ${formTitle}`}</Text>
    </View>
    <View style={styles.runHeaderRule} />
  </View>
);

const RunningFooter = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerBrand}>www.restoreyourcore.com · © Restore Your Core®</Text>
    <Text style={styles.footerPage} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
  </View>
);

/**
 * The logo header for every form that isn't a workbook: logo on the left, the
 * program and form title on the right. Workbooks use `RunningHeader` instead so
 * they match the printed workbook.
 */
const BrandedHeader = ({
  formTitle,
  spaceName,
  logoSrc,
}: {
  formTitle: string;
  spaceName?: string;
  logoSrc?: string;
}) => (
  <>
    <View style={styles.header} fixed>
      {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : <View style={styles.logo} />}
      <View style={styles.headerRight}>
        <Text style={styles.headerEyebrow}>
          {spaceName ? (
            <>
              <Text style={styles.headerEyebrowSpace}>{spaceName}</Text>
              {'  |  FORM SUBMISSION'}
            </>
          ) : (
            'FORM SUBMISSION'
          )}
        </Text>
        <Text style={styles.headerTitle}>{formTitle}</Text>
      </View>
    </View>
    <View style={styles.accentStrip} fixed />
  </>
);

interface SubmissionPdfProps {
  submission: TSubmissionResult;
  formTitle: string;
  questions: TFormQuestion[];
  /** Form sections, used to print a heading above each section's questions. */
  groups?: TFormQuestionGroup[];
  spaceName?: string;
  /** Workbooks get the workbook running header; every other type the logo header. */
  formType?: EFormType;
  /** Printed on the cover's "Name" rule. */
  memberName?: string;
  /** Form description, printed as the cover's topic line. */
  formDescription?: string | null;
  /** Print the workbook title page. Off unless the form opts in. */
  coverEnabled?: boolean;
  /** Eyebrow above the cover title. Falls back to the house line. */
  coverKicker?: string | null;
  /** Badge under the cover title. Falls back to "Interactive Workbook". */
  coverSubtitle?: string | null;
  /** Rasterized cover artwork; omitted when it could not be loaded. */
  coverArtSrc?: string;
  /** Question images, preloaded as data URLs — see `TPdfImageMap`. */
  images?: TPdfImageMap;
  logoSrc?: string;
  /**
   * Hands-On Practice only: the teacher's delivered feedback, printed after the
   * answers. `null` prints a "not shared yet" note; `undefined` prints nothing.
   */
  feedback?: TSubmissionFeedback | null;
}

/** A rejected Hands-On Practice: the teacher's reason instead of feedback. */
const RejectionPdfSection = ({ reason, rejectedAt }: { reason: string; rejectedAt: string }) => (
  <View wrap={false}>
    <View style={styles.sectionHeading} minPresenceAhead={SECTION_HEADING_PRESENCE}>
      <Text style={styles.sectionHeadingTitle}>Teacher review</Text>
      <View style={styles.sectionHeadingRule} />
    </View>
    <View style={[styles.feedbackNote, { backgroundColor: '#fdecee' }]}>
      <View style={[styles.feedbackBar, { backgroundColor: '#c62828' }]} />
      <View style={styles.feedbackPanel}>
        <Text style={styles.feedbackFrom}>
          <Text style={styles.feedbackFromName}>Your teacher asked you to submit this form again</Text>
        </Text>
        <Text style={styles.feedbackDate}>{`Rejected ${formatDate(rejectedAt)}`}</Text>
        <Text style={[styles.feedbackText, { marginBottom: 8 }]}>{reason}</Text>
      </View>
    </View>
  </View>
);

/**
 * The teacher's feedback on a Hands-On Practice submission, printed after the
 * answers as a note from the teacher. Questions the teacher left blank are left
 * out; attached files print as links.
 */
const FeedbackPdfSection = ({ feedback }: { feedback: TSubmissionFeedback | null }) => {
  const heading = (
    <View style={styles.sectionHeading} wrap={false} minPresenceAhead={SECTION_HEADING_PRESENCE}>
      <Text style={styles.sectionHeadingTitle}>Teacher feedback</Text>
      <View style={styles.sectionHeadingRule} />
    </View>
  );

  if (!feedback) {
    return (
      <View wrap={false}>
        {heading}
        <Text style={styles.feedbackPending}>Your teacher hasn&apos;t shared feedback on this submission yet.</Text>
      </View>
    );
  }

  // Same reduction as the form page's feedback note, so the two never disagree.
  const items = getFeedbackItems(feedback).map(item => ({
    question: item.question,
    lines: item.lines,
    grades: item.grades,
    files: item.fileUrls,
  }));
  const wasUpdated = wasFeedbackUpdated(feedback);

  return (
    <>
      {heading}
      <View style={styles.feedbackNote}>
        <View style={styles.feedbackBar} />
        <View style={styles.feedbackPanel}>
          <Text style={styles.feedbackFrom}>
            {'From '}
            <Text style={styles.feedbackFromName}>{feedback.teacher.name}</Text>
          </Text>
          <Text style={styles.feedbackDate}>
            {formatDate(feedback.submittedAt)}
            {wasUpdated ? `  ·  updated ${formatDate(feedback.updatedAt)}` : ''}
          </Text>
          {items.map(({ question, lines, grades, files }) => (
            <View key={question.id} style={styles.feedbackItem} wrap={false}>
              <Text style={styles.feedbackPrompt}>{stripHtml(question.questionText)}</Text>
              {lines.length > 0 && <Text style={styles.feedbackText}>{lines.join('\n')}</Text>}
              {grades.length > 0 && (
                <View style={styles.feedbackGrades}>
                  {grades.map((grade, index) => (
                    <View
                      key={grade.rowId}
                      style={
                        index < grades.length - 1
                          ? [styles.gradeRow, styles.feedbackGradeRow]
                          : [styles.gradeRow, { borderBottom: 0 }]
                      }
                    >
                      <Text style={styles.gradeLabel}>{grade.label}</Text>
                      <Text style={styles.feedbackGradeValue}>{grade.value}</Text>
                    </View>
                  ))}
                </View>
              )}
              {files.map(url => (
                <Link key={url} src={url} style={styles.fileLink}>
                  {fileNameFromUrl(url)}
                </Link>
              ))}
            </View>
          ))}
        </View>
      </View>
    </>
  );
};

export const SubmissionPdf = ({
  submission,
  formTitle,
  questions,
  groups,
  spaceName,
  formType,
  memberName,
  formDescription,
  coverEnabled = false,
  coverKicker,
  coverSubtitle,
  coverArtSrc,
  images,
  logoSrc,
  feedback,
}: SubmissionPdfProps) => {
  const isWorkbook = formType === EFormType.WORKBOOK;
  const cleanSpaceName = stripHtml(spaceName);
  const sortedQuestions = [...questions].sort((a, b) => a.position - b.position);
  const groupTitleById = new Map((groups ?? []).map(group => [group.id, group.title]));
  const answersByQuestion = new Map(submission.answers.map(a => [a.questionId, a]));
  const answerableQuestions = sortedQuestions.filter(q => isAnswerableQuestionType(q.questionType));
  const answeredCount = answerableQuestions.filter(q => isAnswerPresent(q, answersByQuestion.get(q.id))).length;

  const startedOn = formatDate(submission.startedAt);
  const submittedOn = formatDate(submission.submittedAt);

  // Blank means "use the house line"; a single space means the admin wanted the
  // line gone, so an explicitly empty string has to stay empty rather than fall
  // back to the default.
  const kickerLine = coverKicker === null || coverKicker === undefined ? DEFAULT_COVER_KICKER : coverKicker.trim();
  const badgeLine = coverSubtitle === null || coverSubtitle === undefined ? DEFAULT_COVER_BADGE : coverSubtitle.trim();
  const coverTopics = stripHtml(formDescription ?? undefined);

  return (
    <Document title={`${formTitle} - Submission #${submission.submissionNumber}`} author="Restore Your Core">
      {/* Cover — the workbook's title page, with the member's name and start date
          written onto the rules they'd otherwise fill in by hand. Opt-in: members
          who only ever fill the form in on screen don't need a title sheet. */}
      {coverEnabled ? (
        <Page size="A4" style={styles.coverPage}>
          {/* The artwork carries the top rule and the corner logo, so the cover
              skips the running header rather than printing a second one over it. */}
          {coverArtSrc ? <Image fixed src={coverArtSrc} style={styles.coverArt} /> : null}
          <View style={styles.cover}>
            {!coverArtSrc && logoSrc ? (
              <Image src={logoSrc} style={[styles.logo, { alignSelf: 'center', marginBottom: 24 }]} />
            ) : null}
            <Text style={styles.coverTitle}>{formTitle}</Text>
            {kickerLine ? <Text style={styles.coverKicker}>{kickerLine.toUpperCase()}</Text> : null}
            {cleanSpaceName ? <Text style={styles.coverModule}>{cleanSpaceName.toUpperCase()}</Text> : null}

            {badgeLine ? (
              <View style={styles.coverBadgeRow}>
                <View style={styles.coverBadge}>
                  <Text style={styles.coverBadgeText}>{badgeLine}</Text>
                </View>
              </View>
            ) : null}

            {coverTopics ? <Text style={styles.coverTopics}>{coverTopics}</Text> : null}

            <View style={styles.coverSpacer} />

            <View style={styles.coverFields}>
              <View style={styles.coverField}>
                <Text style={styles.coverFieldValue}>{memberName ?? ''}</Text>
                <View style={styles.coverFieldRule} />
                <Text style={styles.coverFieldLabel}>Name</Text>
              </View>
              <View style={styles.coverField}>
                <Text style={styles.coverFieldValue}>{formatDay(submission.startedAt)}</Text>
                <View style={styles.coverFieldRule} />
                <Text style={styles.coverFieldLabel}>Date started</Text>
              </View>
            </View>
          </View>
          <RunningFooter />
        </Page>
      ) : null}

      <Page size="A4" style={isWorkbook ? styles.page : [styles.page, styles.brandedPage]} wrap>
        {isWorkbook ? (
          <RunningHeader formTitle={formTitle} />
        ) : (
          <BrandedHeader formTitle={formTitle} spaceName={cleanSpaceName} logoSrc={logoSrc} />
        )}

        <View style={styles.metaWrap}>
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Submission</Text>
              <Text style={styles.metaValue}>#{submission.submissionNumber}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Started</Text>
              <Text style={styles.metaValue}>{startedOn}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Submitted</Text>
              <Text style={styles.metaValue}>{submittedOn}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Answered</Text>
              <Text style={styles.metaValue}>
                {answeredCount} / {answerableQuestions.length}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.body}>
          {sortedQuestions.map((question, questionIndex) => {
            // Questions are ordered by position, which keeps a section contiguous,
            // so a heading goes in wherever the section changes. Ungrouped runs are
            // labelled too — otherwise it's unclear whether they belong to the
            // section above them.
            const sectionId = question.groupId ?? null;
            const startsSection =
              questionIndex === 0 || (sortedQuestions[questionIndex - 1].groupId ?? null) !== sectionId;

            return (
              <React.Fragment key={question.id}>
                {/* One reservation per section, so a section title is never
                    printed alone at the foot of a page. */}
                {startsSection && (
                  <View style={styles.sectionHeading} wrap={false} minPresenceAhead={SECTION_HEADING_PRESENCE}>
                    <Text style={styles.sectionHeadingTitle}>
                      {sectionId ? (groupTitleById.get(sectionId) ?? 'Section') : 'General'}
                    </Text>
                    <View style={styles.sectionHeadingRule} />
                  </View>
                )}
                <QuestionPdfBlock
                  index={answerableQuestions.indexOf(question)}
                  question={question}
                  answer={answersByQuestion.get(question.id)}
                  images={images}
                />
              </React.Fragment>
            );
          })}
          {submission.rejectedAt ? (
            <RejectionPdfSection reason={submission.rejectionReason ?? ''} rejectedAt={submission.rejectedAt} />
          ) : (
            feedback !== undefined && <FeedbackPdfSection feedback={feedback} />
          )}
        </View>

        <RunningFooter />
      </Page>
    </Document>
  );
};

const sanitizeFilename = (name: string): string =>
  name
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);

/**
 * Render the site SVG logo to a PNG data URL so @react-pdf/renderer can embed
 * it via <Image>. We recolor `currentColor` to the brand pink (matches the
 * site header's tinted logo) before rasterizing.
 */
const loadLogoAsPng = async (): Promise<string | undefined> => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const res = await fetch('/site-logo.svg');
    if (!res.ok) {
      return undefined;
    }
    const raw = await res.text();
    const recolored = raw.replace(/currentColor/g, BRAND);
    const blob = new Blob([recolored], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new window.Image();
        el.onload = () => resolve(el);
        el.onerror = err => reject(err);
        el.src = url;
      });
      // Render at ~3x for sharp PDF output.
      const targetWidth = 330;
      const ratio = img.naturalWidth ? img.naturalHeight / img.naturalWidth : 0.44;
      const targetHeight = Math.round(targetWidth * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return undefined;
      }
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.warn('Failed to load site logo for PDF', error);
    return undefined;
  }
};

/**
 * Every image the workbook needs, keyed by the src as it was authored, resolved
 * to a PNG/JPEG data URL.
 *
 * react-pdf renders synchronously and only understands PNG and JPEG, while the
 * media manager stores WebP and the rich editor stores cross-origin CDN URLs —
 * so images are fetched, converted and inlined before the document is built. An
 * image missing from the map failed to load and is skipped: a broken src throws
 * mid-render and takes the whole PDF with it.
 */
export type TPdfImageMap = Map<string, string>;

/** Downscale cap for re-encoded images — beyond this is invisible in print. */
const PDF_IMAGE_MAX_WIDTH = 1200;

const readBlobAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Fetch one image and hand back something react-pdf can embed, or `undefined`
 * when it could not be loaded (404, CORS, an unreadable format).
 */
const loadImageForPdf = async (url: string): Promise<string | undefined> => {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      return undefined;
    }
    const blob = await res.blob();

    // Already a format react-pdf reads: inline it as-is rather than paying for a
    // re-encode that would only make a photograph bigger.
    if (blob.type === 'image/png' || blob.type === 'image/jpeg') {
      return await readBlobAsDataUrl(blob);
    }

    // Anything else (WebP, AVIF, SVG) goes through a canvas. The source is a
    // blob: URL, which is same-origin, so the canvas is never tainted.
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new window.Image();
        el.onload = () => resolve(el);
        el.onerror = err => reject(err);
        el.src = objectUrl;
      });
      const scale = img.naturalWidth > PDF_IMAGE_MAX_WIDTH ? PDF_IMAGE_MAX_WIDTH / img.naturalWidth : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return undefined;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    console.warn('Failed to load image for PDF', url, error);
    return undefined;
  }
};

/** Every image src a question carries: inline HTML `<img>` and block media. */
const collectImageSources = (questions: TFormQuestion[]): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  const sources = new Set<string>();

  const fromHtml = (html: string | null | undefined) => {
    if (!html?.includes('<img')) {
      return;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const img of doc.querySelectorAll('img')) {
      const src = img.getAttribute('src');
      if (src) {
        sources.add(src);
      }
    }
  };

  for (const question of questions) {
    fromHtml(question.questionText);
    fromHtml(question.questionBody);
    if (question.questionType === 'text_block') {
      for (const block of getTextBlocks(question.metadata)) {
        fromHtml(block.body);
        if (block.media?.url) {
          sources.add(block.media.url);
        }
      }
    }
  }

  return [...sources];
};

/**
 * Resolve every collected src in parallel. Keys stay the *authored* src so the
 * renderer can look an image up without repeating the media-path handling.
 */
const loadPdfImages = async (sources: string[]): Promise<TPdfImageMap> => {
  const entries = await Promise.all(
    sources.map(async src => {
      // A media-manager path needs the CDN prefix; an editor upload is already
      // absolute and `getMediaUrl` passes it through untouched.
      let absolute: string | undefined;
      try {
        absolute = getMediaUrl(src);
      } catch {
        // getMediaUrl throws when VITE_CDN_BASE_URL is unset — skip, don't crash.
        return [src, undefined] as const;
      }
      if (!absolute) {
        return [src, undefined] as const;
      }
      return [src, await loadImageForPdf(absolute)] as const;
    }),
  );
  return new Map(entries.filter((entry): entry is readonly [string, string] => !!entry[1]));
};

/** Where the workbook cover artwork lives in `public/`. */
const COVER_ART_URL = '/workbook-cover-bg.jpg';

/**
 * Fetch the cover artwork as a data URL. @react-pdf/renderer can take a URL
 * directly, but inlining it keeps the render self-contained (and a failed fetch
 * degrades to a plain cover instead of stalling the document).
 */
const loadCoverArt = async (): Promise<string | undefined> => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const res = await fetch(COVER_ART_URL);
    if (!res.ok) {
      return undefined;
    }
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to load workbook cover artwork for PDF', error);
    return undefined;
  }
};

/**
 * An options object rather than a positional list: the workbook cover needs the
 * member's name, the form description and the sections on top of the submission
 * itself, and six positional arguments at two call sites is how they get mixed up.
 */
export interface TDownloadSubmissionPdfOptions {
  submission: TSubmissionResult;
  formTitle: string;
  questions: TFormQuestion[];
  groups?: TFormQuestionGroup[];
  spaceName?: string;
  formType?: EFormType;
  memberName?: string;
  formDescription?: string | null;
  /** From the form's settings — the cover is off unless a form opts in. */
  coverEnabled?: boolean;
  coverKicker?: string | null;
  coverSubtitle?: string | null;
}

export const downloadSubmissionPdf = async ({
  submission,
  formTitle,
  questions,
  groups,
  spaceName,
  formType,
  memberName,
  formDescription,
  coverEnabled = false,
  coverKicker,
  coverSubtitle,
}: TDownloadSubmissionPdfOptions): Promise<void> => {
  // The logo goes on the cover and on the header of every non-workbook form; the
  // artwork only on the cover. Neither is fetched when nothing will print it.
  const needsLogo = coverEnabled || formType !== EFormType.WORKBOOK;
  const [logoSrc, coverArtSrc] = await Promise.all([
    needsLogo ? loadLogoAsPng() : undefined,
    coverEnabled ? loadCoverArt() : undefined,
  ]);
  // Hands-On Practice prints the teacher's feedback after the answers. A failed
  // fetch leaves it out rather than blocking the student's own answers.
  const feedback =
    isTeacherReviewedFormType(formType) && !submission.rejectedAt
      ? await fetchSubmissionFeedback(submission.id).catch((error: unknown) => {
          console.warn('Failed to load teacher feedback for PDF', error);
          return undefined;
        })
      : undefined;
  // react-pdf can't fetch images itself, so every one the questions reference is
  // resolved to a data URL first.
  const images = await loadPdfImages(collectImageSources([...questions, ...(feedback?.feedbackForm.questions ?? [])]));
  const blob = await pdf(
    <SubmissionPdf
      submission={submission}
      formTitle={formTitle}
      questions={questions}
      groups={groups}
      spaceName={spaceName}
      formType={formType}
      memberName={memberName}
      formDescription={formDescription}
      coverEnabled={coverEnabled}
      coverKicker={coverKicker}
      coverSubtitle={coverSubtitle}
      coverArtSrc={coverArtSrc}
      images={images}
      logoSrc={logoSrc}
      feedback={feedback}
    />,
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilename(formTitle)}-submission-${submission.submissionNumber}.pdf`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
